<template>
  <LoadingDiv v-if="!isReady" />

  <section class="tariff-mini-shell" v-else>
    <div class="tariff-mini-scroll-marker" ref="tariffShelf"></div>

    <section class="tariff-mini-card">
      <div class="tariff-mini-intro">
        <div>
          <label class="active teal-text">Tariff Shelf</label>
          <h6>{{ schedule || "Provider tariff schedule" }}</h6>
          <p>Review existing items and adjust agreed provider prices.</p>
        </div>
        <div class="tariff-mini-context">
          <span>{{ scheme || "Scheme not loaded" }}</span>
          <span>{{ lastUpdatedLabel }}</span>
        </div>
      </div>

      <section class="tariff-mini-search-card">
        <div>
          <label class="active teal-text">Search Tariff</label>
          <h6>Find a procedure or service</h6>
        </div>
        <form class="tariff-mini-search" @submit.prevent>
          <i class="material-icons">search</i>
          <input type="search" v-model="searchItem" placeholder="Search tariff item" autocomplete="off" />
          <button type="button" @click.prevent="handleClearSearch" v-if="searchItem" title="Clear search">
            <i class="material-icons">close</i>
          </button>
        </form>
      </section>

      <div class="progress tariff-mini-progress" v-if="isLoading">
        <div class="indeterminate"></div>
      </div>

      <section class="tariff-mini-list" v-if="tariffs.length">
        <div class="tariff-mini-head">
          <span>#</span>
          <span>Tariff Item</span>
          <span>Price</span>
          <span>Action</span>
        </div>

        <div
          class="tariff-mini-row"
          :class="{ 'is-editing': tariff.id == isEditing }"
          v-for="(tariff, index) in tariffs"
          :key="tariff.id"
        >
          <template v-if="tariff.id != isEditing">
            <span class="tariff-mini-index">{{ numberBadge(index) }}</span>
            <div class="tariff-mini-item">
              <strong>{{ tariff.item }}</strong>
              <small>{{ tariffActiveDate(tariff.created_at) }}</small>
            </div>
            <div class="tariff-mini-price">
              <span>&#8358;{{ money(tariff.price) }}</span>
            </div>
            <button type="button" class="tariff-mini-edit-action" @click.prevent="handleEdit(tariff)" title="Adjust price">
              <i class="material-icons">edit</i>
            </button>
          </template>

          <form class="tariff-mini-edit-form" @submit.prevent="handleEditConfirm(tariff)" v-else>
            <span class="tariff-mini-index">{{ numberBadge(index) }}</span>
            <div class="tariff-mini-edit-item">
              <label class="active teal-text">Tariff Item</label>
              <strong>{{ item }}</strong>
            </div>
            <div class="input-field">
              <input type="number" step="0.01" min="0" v-model="price" />
              <label class="active teal-text">Price</label>
            </div>
            <div class="tariff-mini-edit-actions">
              <button type="button" class="btn-flat tariff-mini-cancel-action" @click.prevent="handleCancel()">
                Cancel
              </button>
              <button class="btn tariff-mini-update-action" :class="{ disabled: isSubmitting }">
                Update
              </button>
            </div>
          </form>
        </div>
      </section>

      <section class="tariff-mini-empty" v-if="!tariffs.length && !isLoading">
        <i class="material-icons">inventory_2</i>
        <h6>No tariff item found</h6>
        <p>There are no existing tariff items matching this view.</p>
      </section>

      <section class="tariff-mini-error" v-if="error">
        {{ error }}
      </section>
    </section>

    <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="tariffs.length" />
  </section>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import getItemsOfItemsOfItems from "@/composables/getItemsOfItemsOfItems";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import nativeUi from "@/ui/native-ui";

export default {
  name: "TariffMiniComponent",
  components: { LoadingDiv, PaginationDiv },
  props: ["provider"],
  setup(props) {
    const response = ref(null);
    const schedule = ref(null);
    const scheme = ref(null);
    const updated = ref(null);
    const error = ref(null);
    const isEditing = ref(null);
    const isSubmitting = ref(false);
    const isReady = ref(false);
    const isLoading = ref(true);
    const currUrl = ref(null);
    const currHook = ref(null);
    const item = ref(null);
    const price = ref(null);
    const searchItem = ref(null);
    const tariffShelf = ref(null);

    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    const money = (value) => {
      return Number(value || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const formatUpdatedDate = (value) => {
      if (!value) {
        return null;
      }

      const safeValue = typeof value === "string" ? value.replace(" ", "T") : value;
      const date = new Date(safeValue);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date.toLocaleString("en", {
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const lastUpdatedLabel = computed(() => {
      const date = formatUpdatedDate(updated.value);

      return date ? `Updated ${date}` : "Update date unavailable";
    });

    const tariffActiveDate = (value) => {
      if (!value) {
        return "Active date unavailable";
      }

      const safeValue = typeof value === "string" ? value.replace(" ", "T") : value;
      const date = new Date(safeValue);

      if (Number.isNaN(date.getTime())) {
        return "Active date unavailable";
      }

      return `Active since ${date.toLocaleDateString("en", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })}`;
    };

    const getTariffsFromApi = (table, id1, id2) => {
      (async () => {
        const { res, err } = await getItemsOfItemsOfItems(table, id1, id2);

        if (res.value) {
          response.value = res.value.data;
          error.value = null;
        } else {
          error.value = err.value?.message || err.value || "Unable to load tariff records.";
        }

        isReady.value = true;
        isLoading.value = false;
      })();
    };

    const scheduleReload = () => {
      (async () => {
        if (!props.provider?.schedule_id) {
          isReady.value = true;
          isLoading.value = false;
          error.value = "This provider does not have a tariff schedule.";
          return;
        }

        const { res } = await getItemsOfItems("schedule", props.provider.schedule_id);

        if (res.value) {
          schedule.value = res.value.data.plan.name;
          scheme.value = res.value.data.plan.scheme.name;
          updated.value = res.value.data.updated_at;
        }
      })();
    };

    const tariffRestart = () => {
      if (!props.provider?.schedule_id || !props.provider?.id) {
        return;
      }

      currUrl.value = "tariff/schedule";
      currHook.value = "?";
      getTariffsFromApi(currUrl.value, props.provider.schedule_id, props.provider.id);
    };

    onMounted(() => {
      scheduleReload();
      tariffRestart();
    });

    const handleEdit = (tariff) => {
      isEditing.value = tariff.id;
      item.value = tariff.item;
      price.value = tariff.price;
    };

    const handleCancel = () => {
      isEditing.value = null;
      item.value = null;
      price.value = null;
    };

    const handleEditConfirm = async (tariff) => {
      if (isSubmitting.value) {
        return;
      }

      isSubmitting.value = true;

      const newVal = {
        item: item.value,
        price: price.value,
        schedule_id: props.provider.schedule_id,
        scope: props.provider.id,
      };

      const { res, err } = await postItem("tariff/add", newVal);

      if (res.value) {
        response.value.data.forEach((x) => {
          if (x.id == tariff.id) {
            x.item = item.value;
            x.price = price.value;
            x.created_at = res.value.data.created_at || x.created_at;
          }
        });
        nativeUi.toast({ html: "Tariff updated", classes: "teal" });
      } else {
        error.value = `Error: ${item.value} not updated! (${err.value})`;
      }

      isSubmitting.value = false;
      handleCancel();
    };

    const handleClearSearch = () => {
      isLoading.value = true;
      searchItem.value = null;
      tariffRestart();
    };

    const handlePage = (page) => {
      tariffShelf.value?.scrollIntoView({ behavior: "smooth", block: "start" });
      isLoading.value = true;
      getTariffsFromApi(
        currUrl.value,
        props.provider.schedule_id,
        `${props.provider.id}${currHook.value}page=${page}`
      );
    };

    watch(searchItem, (value) => {
      if (value) {
        isLoading.value = true;
        currUrl.value = "tariff/schedule/search";
        currHook.value = `?q=${value}&`;
        getTariffsFromApi(
          currUrl.value,
          props.provider.schedule_id,
          `${props.provider.id}?q=${value}`
        );
      }
    });

    const tariffs = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      handleEdit,
      handleEditConfirm,
      handleCancel,
      handleClearSearch,
      handlePage,
      numberBadge,
      money,
      tariffActiveDate,
      error,
      isEditing,
      isSubmitting,
      isReady,
      isLoading,
      searchItem,
      item,
      price,
      schedule,
      scheme,
      updated,
      lastUpdatedLabel,
      tariffs,
      allPages,
      tariffShelf,
    };
  },
};
</script>

<style scoped>
.tariff-mini-shell {
  color: #263238;
  display: grid;
  gap: 14px;
}

.tariff-mini-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  overflow: hidden;
}

.tariff-mini-scroll-marker {
  height: 1px;
  scroll-margin-top: 76px;
}

.tariff-mini-intro,
.tariff-mini-search-card,
.tariff-mini-list,
.tariff-mini-empty {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.tariff-mini-intro {
  align-items: center;
  border-bottom: 1px solid #eef3f5;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 18px 20px;
}

.tariff-mini-intro label,
.tariff-mini-search-card label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.tariff-mini-intro h6,
.tariff-mini-search-card h6,
.tariff-mini-empty h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
}

.tariff-mini-intro p,
.tariff-mini-empty p {
  color: #607d8b;
  line-height: 1.55;
  margin: 7px 0 0;
}

.tariff-mini-context {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tariff-mini-context span {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 999px;
  color: #455a64;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 11px;
}

.tariff-mini-search-card {
  align-items: center;
  border-bottom: 1px solid #eef3f5;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(180px, 0.8fr) minmax(260px, 1.4fr);
  padding: 18px 20px;
}

.tariff-mini-search {
  align-items: center;
  border: 1px solid #b7c5ca;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-height: 48px;
  padding: 0 12px;
}

.tariff-mini-search i {
  color: #00897b;
  font-size: 21px;
}

.tariff-mini-search input {
  border: 0 !important;
  box-shadow: none !important;
  color: #263238;
  font-size: 1rem;
  height: 44px;
  margin: 0;
}

.tariff-mini-search button {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;
}

.tariff-mini-progress {
  margin: -4px 0 0;
}

.tariff-mini-list {
  overflow: hidden;
  padding-bottom: 14px;
}

.tariff-mini-head,
.tariff-mini-row {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: 56px minmax(220px, 1.6fr) minmax(140px, 0.7fr) 72px;
}

.tariff-mini-head {
  background: rgba(255, 255, 255, 0.72);
  border-bottom: 1px solid #eeeeee;
  color: #607d8b;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 13px 20px;
  text-transform: uppercase;
}

.tariff-mini-row {
  border-bottom: 1px solid #eeeeee;
  min-height: 82px;
  padding: 18px 20px;
}

.tariff-mini-row:last-child {
  border-bottom: 0;
}

.tariff-mini-row.is-editing {
  background: #fffdf5;
}

.tariff-mini-index {
  color: #607d8b;
  font-weight: 700;
}

.tariff-mini-item strong {
  color: #263238;
  display: block;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

.tariff-mini-item small {
  color: #607d8b;
  display: block;
  font-size: 0.84rem;
  margin-top: 4px;
}

.tariff-mini-price span {
  background: #f1f8e9;
  border-radius: 6px;
  color: #33691e;
  display: inline-flex;
  font-weight: 800;
  min-width: 128px;
  padding: 10px 12px;
}

.tariff-mini-edit-action {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  color: #00897b;
  cursor: pointer;
  display: inline-flex;
  height: 38px;
  justify-content: center;
  padding: 0;
  width: 38px;
}

.tariff-mini-edit-action i {
  font-size: 19px;
}

.tariff-mini-edit-form {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-column: 1 / -1;
  grid-template-columns: 56px minmax(220px, 1.4fr) minmax(140px, 0.6fr) auto;
}

.tariff-mini-edit-form .input-field {
  margin: 0;
}

.tariff-mini-edit-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.tariff-mini-edit-item label {
  line-height: 1;
}

.tariff-mini-edit-item strong {
  color: #263238;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
}

.tariff-mini-edit-form input {
  border-bottom-color: #b0bec5;
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

.tariff-mini-edit-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.tariff-mini-cancel-action {
  color: #455a64;
  font-weight: 700;
  text-transform: none;
}

.tariff-mini-update-action {
  background: #00897b;
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

.tariff-mini-empty,
.tariff-mini-error {
  padding: 32px 20px;
  text-align: center;
}

.tariff-mini-empty i {
  color: #90a4ae;
  font-size: 32px;
}

.tariff-mini-error {
  background: #fff8e1;
  border-top: 1px solid #ffe082;
  color: #795548;
  font-weight: 700;
}

@media only screen and (max-width: 900px) {
  .tariff-mini-intro,
  .tariff-mini-search-card {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .tariff-mini-intro {
    flex-direction: column;
  }

  .tariff-mini-context {
    align-items: flex-start;
  }

  .tariff-mini-head {
    display: none;
  }

  .tariff-mini-row,
  .tariff-mini-edit-form {
    align-items: flex-start;
    grid-template-columns: 42px 1fr;
  }

  .tariff-mini-price,
  .tariff-mini-edit-action,
  .tariff-mini-edit-item,
  .tariff-mini-edit-form .input-field,
  .tariff-mini-edit-actions {
    grid-column: 2;
  }

  .tariff-mini-edit-actions {
    justify-content: flex-start;
  }
}
</style>
